import { PencilIcon, TrashIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { cn } from "@win-reality/react-ui";
import { useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";

import {
  addToLineup,
  clearCurrentCatcher,
  clearCurrentPitcher,
  clearRoster,
  removeFromLineup,
  reorderLineup,
} from "../store/slices/game";
import type { RootState } from "../store/store";
import type { IPlayer, TTeamSides } from "../store/types";
import { LineupList } from "./LineupList";

/** One team's setup: what the lineup builder shows and edits. */
export type SetupTeam = {
  name: string;
  roster: IPlayer[];
  currentLineup: IPlayer[];
  currentPitcher: IPlayer;
  currentCatcher: IPlayer;
};

type TeamLineupPanelProps = {
  side: TTeamSides;
  onEditTeam: () => void;
  onSelectPlayer: (type: "pitcher" | "catcher") => void;
};

/**
 * TeamLineupPanel - Full lineup builder for one team, on the tagging store
 */
export const TeamLineupPanel = ({
  side,
  onEditTeam,
  onSelectPlayer,
}: TeamLineupPanelProps) => {
  const dispatch = useDispatch();
  const team = useSelector((state: RootState) => state.game.rosters[side]);

  return (
    <TeamLineupView
      side={side}
      team={team}
      onEditTeam={onEditTeam}
      onSelectPlayer={onSelectPlayer}
      onClearTeam={() => dispatch(clearRoster({ team: side }))}
      onClearPitcher={() => dispatch(clearCurrentPitcher({ team: side }))}
      onClearCatcher={() => dispatch(clearCurrentCatcher({ team: side }))}
      onAddToLineup={(player) => dispatch(addToLineup({ team: side, player }))}
      onRemoveFromLineup={(player) =>
        dispatch(removeFromLineup({ team: side, player, uuid: player.uuid }))
      }
      onReorderLineup={(lineup) =>
        dispatch(reorderLineup({ team: side, lineup }))
      }
    />
  );
};

type TeamLineupViewProps = TeamLineupPanelProps & {
  team: SetupTeam;
  onClearTeam: () => void;
  onClearPitcher: () => void;
  onClearCatcher: () => void;
  onAddToLineup: (player: IPlayer) => void;
  onRemoveFromLineup: (player: IPlayer) => void;
  onReorderLineup: (lineup: IPlayer[]) => void;
};

/**
 * TeamLineupView - Full lineup builder for one team
 *
 * Shows:
 * - Team header with edit/delete actions
 * - Pitcher selection
 * - Catcher selection
 * - Batting order with drag-and-drop reordering
 */
export const TeamLineupView = ({
  side,
  team,
  onEditTeam,
  onSelectPlayer,
  onClearTeam,
  onClearPitcher,
  onClearCatcher,
  onAddToLineup,
  onRemoveFromLineup,
  onReorderLineup,
}: TeamLineupViewProps) => {
  const {
    name: teamName,
    roster,
    currentLineup,
    currentPitcher,
    currentCatcher,
  } = team;

  // Players in lineup (with position numbers)
  const lineupPlayerUuids = useMemo(
    () => new Set(currentLineup.map((p) => p.uuid)),
    [currentLineup],
  );

  // Players not in lineup (available to add)
  const availablePlayers = useMemo(
    () => roster.filter((p) => !lineupPlayerUuids.has(p.uuid)),
    [roster, lineupPlayerUuids],
  );

  const label = side === "away" ? "Away Team" : "Home Team";

  return (
    <div className="flex h-full flex-1 flex-col overflow-hidden px-16">
      {/* Scrollable Content */}
      <div className="small-scrollbar flex-1 overflow-y-auto pb-4 px-4">
        {/* Team Header */}
        <div className="border-b border-gray-200 py-4">
          <span className="text-sm font-semibold text-text-light">{label}</span>
          <div className="mt-1 flex items-center justify-between">
            <h2 className="text-xl text-text-light">{teamName}</h2>
            <div className="flex gap-2">
              <button
                onClick={onEditTeam}
                className="rounded p-2 text-accent-dark-3 transition-colors hover:bg-gray-100"
                aria-label="Edit team"
              >
                <PencilIcon className="h-5 w-5" />
              </button>
              <button
                onClick={onClearTeam}
                className="rounded p-2 text-error transition-colors hover:bg-error/10"
                aria-label="Delete team"
              >
                <TrashIcon className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>
        {/* Pitcher Section */}
        <div className="border-b border-gray-200 py-6">
          <div className="flex items-center justify-between">
            <span className="text-sm font-semibold text-text-light">
              Pitcher
            </span>
            {!currentPitcher?.uuid && (
              <button
                onClick={() => onSelectPlayer("pitcher")}
                className="text-sm font-medium text-blue hover:underline"
              >
                Select Player
              </button>
            )}
          </div>
          {currentPitcher?.uuid && (
            <div className="pt-2">
              <PlayerCard
                player={currentPitcher}
                badgeContent="P"
                badgeType="pitcher"
                onRemove={onClearPitcher}
              />
            </div>
          )}
        </div>

        {/* Catcher Section */}
        <div className="border-b border-gray-200 py-6">
          <div className="flex items-center justify-between">
            <span className="text-sm font-semibold text-text-light">
              Catcher
            </span>
            {!currentCatcher?.uuid && (
              <button
                onClick={() => onSelectPlayer("catcher")}
                className="text-sm font-medium text-blue hover:underline"
              >
                Select Player
              </button>
            )}
          </div>
          {currentCatcher?.uuid && (
            <div className="pt-2">
              <PlayerCard
                player={currentCatcher}
                badgeContent="C"
                badgeType="catcher"
                onRemove={onClearCatcher}
              />
            </div>
          )}
        </div>

        {/* Batting Order Section */}
        <div className="mt-6">
          <div className="mb-1">
            <span className="text-sm font-semibold text-text-light">
              Batting Order
            </span>
          </div>
          <p className="mb-4 text-sm text-accent-dark-3">
            Add your batters and drag to reorder
          </p>

          <LineupList
            lineupPlayers={currentLineup}
            availablePlayers={availablePlayers}
            onAddToLineup={onAddToLineup}
            onRemoveFromLineup={onRemoveFromLineup}
            onReorder={onReorderLineup}
          />
        </div>
      </div>
    </div>
  );
};

/**
 * PlayerCard - Display a selected player (pitcher/catcher) with remove option
 */
type PlayerCardProps = {
  player: IPlayer;
  badgeContent: string;
  badgeType: "pitcher" | "catcher";
  onRemove: () => void;
};

const PlayerCard = ({
  player,
  badgeContent,
  badgeType,
  onRemove,
}: PlayerCardProps) => {
  const displayName = `${player.firstName} ${player.lastName}`;

  return (
    <div className="flex items-center justify-between rounded border border-gray-200 bg-white px-2 py-2">
      <div className="flex items-center gap-3">
        <div
          className={cn(
            "flex h-8 w-8 items-center justify-center rounded text-sm font-semibold text-white",
            badgeType === "pitcher" && "bg-badge-pitcher",
            badgeType === "catcher" && "bg-badge-catcher",
          )}
        >
          {badgeContent}
        </div>
        <span className="text-sm font-medium text-text-light">
          {displayName}
        </span>
      </div>
      <button
        onClick={onRemove}
        className="rounded p-1 text-accent-dark-3 transition-colors hover:bg-gray-100 hover:text-text-light"
        aria-label={`Remove ${displayName}`}
      >
        <XMarkIcon className="h-5 w-5" />
      </button>
    </div>
  );
};
